import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { AlertCircle } from "lucide-react";
import { toast } from "sonner";

const LivePreviewPanel = ({ state }) => {
  const {
    students,
    tracking,
    setTracking,
    changes,
    sqlOutput,
    csharpOutput,
    dbContextOutput
  } = state;

  const handleTrackingToggle = (checked) => {
    setTracking(checked);
    if (!checked) toast.warning("Change tracking disabled.");
    else toast.success("Change tracking enabled");
  };

  return (
      <>
        {/* Data Table */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Live Data Preview</span>
              <Badge variant="secondary">{students.length} records</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <AnimatePresence>
                    {students.map((student) => (
                        <motion.tr
                            key={student.id}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="border-b border-border"
                        >
                          <TableCell className="font-medium">{student.id}</TableCell>
                          <TableCell>{student.name}</TableCell>
                          <TableCell className="text-muted-foreground">{student.email}</TableCell>
                          <TableCell>
                            <Badge
                                variant={student.status === "Active" ? "default" : "secondary"}
                                className="text-xs"
                            >
                              {student.status}
                            </Badge>
                          </TableCell>
                        </motion.tr>
                    ))}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* SQL + C# + DbContext */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-base">Generated Output</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-border bg-surface-elevated p-4">
              <p className="mb-2 text-xs font-medium text-muted-foreground">ILLUSTRATIVE SQL</p>
              <pre className="overflow-x-auto text-xs sm:text-sm leading-relaxed">
              <code className="text-foreground">{sqlOutput}</code>
            </pre>
            </div>

            <div className="rounded-lg border border-border bg-surface-elevated p-4">
              <p className="mb-2 text-xs font-medium text-muted-foreground">C# EXAMPLE</p>
              <pre className="overflow-x-auto text-xs sm:text-sm leading-relaxed">
              <code className="text-foreground">{csharpOutput}</code>
            </pre>
            </div>

            <div className="rounded-lg border border-border bg-surface-elevated p-4">
              <p className="mb-2 text-xs font-medium text-muted-foreground">DBCONTEXT SNAPSHOT</p>
              <pre className="overflow-x-auto text-xs sm:text-sm leading-relaxed">
              <code className="text-foreground">{dbContextOutput}</code>
            </pre>
            </div>
          </CardContent>
        </Card>

        {/* Change Tracker */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-base">Change Tracker</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="tracking-toggle" className="cursor-pointer">
                Change Tracking
              </Label>
              <Switch id="tracking-toggle" checked={tracking} onCheckedChange={handleTrackingToggle} />
            </div>

            {!tracking && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="flex items-start gap-2 rounded-md bg-warning/10 p-3 text-sm text-warning"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>
                    Tracking is disabled. For this demo, update and delete actions are blocked to
                    reflect “not tracked” behavior.
                  </p>
                </motion.div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <motion.div whileHover={{ scale: 1.05 }} className="rounded-lg border border-border bg-success/10 p-3 text-center">
                <div className="text-2xl font-bold text-success">{changes.added}</div>
                <div className="text-xs text-muted-foreground">Added</div>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} className="rounded-lg border border-border bg-warning/10 p-3 text-center">
                <div className="text-2xl font-bold text-warning">{changes.modified}</div>
                <div className="text-xs text-muted-foreground">Modified</div>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} className="rounded-lg border border-border bg-destructive/10 p-3 text-center">
                <div className="text-2xl font-bold text-destructive">{changes.deleted}</div>
                <div className="text-xs text-muted-foreground">Deleted</div>
              </motion.div>
            </div>
          </CardContent>
        </Card>
      </>
  );
};

export default LivePreviewPanel;
